/**
 * ThumbnailPreloader - Intelligent thumbnail preloading system
 * Based on BurrowTV-LITE-1local template (line ~10234)
 * 
 * Preloads thumbnails for videos that are about to enter viewport
 * Reduces perceived loading time and improves scroll performance
 */

import { getOptimizedThumbnail } from './imageOptimizer';

class ThumbnailPreloaderClass {
  constructor() {
    if (ThumbnailPreloaderClass.instance) {
      return ThumbnailPreloaderClass.instance;
    }
    
    this.preloadedImages = new Set(); // URLs that have been preloaded
    this.preloadQueue = []; // Queue of URLs to preload
    this.isProcessing = false;
    this.maxConcurrent = 3; // Max concurrent preload requests
    this.activeFetches = 0;
    
    ThumbnailPreloaderClass.instance = this;
  }
  
  /**
   * Preload a single thumbnail
   * @param {string} url - Image URL
   * @param {boolean} optimize - Whether to apply optimization (default: true)
   * @returns {Promise<void>}
   */
  preload(url, optimize = true) {
    if (!url || this.preloadedImages.has(url)) {
      return Promise.resolve();
    }
    
    const finalUrl = optimize ? getOptimizedThumbnail(url) : url;
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.preloadedImages.add(url);
        this.preloadedImages.add(finalUrl);
        resolve();
      };
      
      img.onerror = (error) => {
        console.warn(`[ThumbnailPreloader] Failed to preload: ${finalUrl}`, error);
        reject(error);
      };
      
      img.src = finalUrl;
    });
  }
  
  /**
   * Preload multiple thumbnails
   * @param {Array<string>} urls - Array of image URLs
   * @param {boolean} optimize - Whether to apply optimization (default: true)
   * @returns {Promise<void>}
   */
  async preloadBatch(urls, optimize = true) {
    if (!Array.isArray(urls) || urls.length === 0) {
      return;
    }
    
    // Filter out already preloaded
    const toPreload = urls.filter(url => url && !this.preloadedImages.has(url));
    
    if (toPreload.length === 0) {
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ThumbnailPreloader] Preloading ${toPreload.length} thumbnails`);
    }
    
    // Process in batches to avoid overwhelming the browser
    const batchSize = this.maxConcurrent;
    for (let i = 0; i < toPreload.length; i += batchSize) {
      const batch = toPreload.slice(i, i + batchSize);
      await Promise.allSettled(batch.map(url => this.preload(url, optimize)));
    }
  }
  
  /**
   * Preload thumbnails for videos in array
   * @param {Array<object>} videos - Array of video objects
   * @param {number} startIndex - Start index in array
   * @param {number} count - Number of videos to preload (default: 10)
   * @returns {Promise<void>}
   */
  async preloadVideos(videos, startIndex = 0, count = 10) {
    if (!Array.isArray(videos) || videos.length === 0) {
      return;
    }
    
    const endIndex = Math.min(startIndex + count, videos.length);
    const videosToPreload = videos.slice(startIndex, endIndex);
    
    const urls = videosToPreload
      .map(video => video.thumbnailUrl || video.thumbnail)
      .filter(url => url);
    
    await this.preloadBatch(urls, true);
  }
  
  /**
   * Add URLs to preload queue (non-blocking)
   * Queue will be processed in background
   * @param {Array<string>} urls - URLs to add to queue
   */
  queuePreload(urls) {
    if (!Array.isArray(urls)) {
      urls = [urls];
    }
    
    const newUrls = urls.filter(url => 
      url && !this.preloadedImages.has(url) && !this.preloadQueue.includes(url)
    );
    
    this.preloadQueue.push(...newUrls);
    
    // Start processing if not already
    if (!this.isProcessing) {
      this.processQueue();
    }
  }
  
  /**
   * Process preload queue in background
   * @private
   */
  async processQueue() {
    if (this.isProcessing || this.preloadQueue.length === 0) {
      return;
    }
    
    this.isProcessing = true;
    
    while (this.preloadQueue.length > 0 && this.activeFetches < this.maxConcurrent) {
      const url = this.preloadQueue.shift();
      
      if (url && !this.preloadedImages.has(url)) {
        this.activeFetches++;
        
        this.preload(url, true)
          .finally(() => {
            this.activeFetches--;
            
            // Continue processing
            if (this.preloadQueue.length > 0) {
              this.processQueue();
            }
          });
      }
    }
    
    // Check if queue is empty
    if (this.preloadQueue.length === 0 && this.activeFetches === 0) {
      this.isProcessing = false;
    }
  }
  
  /**
   * Preload next N videos from current index
   * Useful in video carousels
   * @param {Array<object>} videos - Array of videos
   * @param {number} currentIndex - Current video index
   * @param {number} lookahead - Number of videos ahead to preload (default: 5)
   */
  preloadAhead(videos, currentIndex, lookahead = 5) {
    if (!Array.isArray(videos) || currentIndex < 0) {
      return;
    }
    
    const startIndex = currentIndex + 1;
    const endIndex = Math.min(startIndex + lookahead, videos.length);
    
    const urls = videos
      .slice(startIndex, endIndex)
      .map(video => video.thumbnailUrl || video.thumbnail)
      .filter(url => url);
    
    this.queuePreload(urls);
  }
  
  /**
   * Check if URL has been preloaded
   * @param {string} url - URL to check
   * @returns {boolean}
   */
  isPreloaded(url) {
    return this.preloadedImages.has(url);
  }
  
  /**
   * Clear preloaded cache
   */
  clearCache() {
    this.preloadedImages.clear();
    this.preloadQueue = [];
    this.isProcessing = false;
    this.activeFetches = 0;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[ThumbnailPreloader] Cache cleared');
    }
  }
  
  /**
   * Get preloader statistics
   * @returns {object}
   */
  getStats() {
    return {
      preloaded: this.preloadedImages.size,
      queued: this.preloadQueue.length,
      active: this.activeFetches,
      isProcessing: this.isProcessing
    };
  }
}

// Create singleton instance
const ThumbnailPreloader = new ThumbnailPreloaderClass();

export default ThumbnailPreloader;
